import { Navigate, Route } from 'react-router-dom';

import { ResponsiveLayout } from '@/components/widgets/t/ResponsiveLayout/ResponsiveLayout.layout';
import { LandstewardInvestmentsPage } from '@/pages/landsteward/pages/investments/Investments.page';
import { LandstewardEditPlotPage } from '@/pages/landsteward/pages/plot/pages/edit/edit-plot.page';
import { LandstewardNewPlotPage } from '@/pages/landsteward/pages/plot/pages/new/new-plot.page';
import { LandstewardPortfolioPage } from '@/pages/landsteward/pages/portfolio/Portfolio.page';
import { ProjectsNewPage } from '@/pages/landsteward/pages/projects/pages/new-project/NewProject.page';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComparePage } from '@/pages/shared/compare/Compare.page';
import { CompareListPage } from '@/pages/shared/compare/CompareList.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { CopilotPage } from '@/pages/shared/copilot/Copilot.page';
import { DisclosurePage } from '@/pages/shared/disclosure/Disclosure.page';
import { HomePage } from '@/pages/shared/home/Home.page';
import { IndicatorsPage } from '@/pages/shared/indicators/Indicators.page';
import { KnowledgePage } from '@/pages/shared/knowledge/Knowledge.page';
import { LandBiodiversityPage } from '@/pages/shared/land/LandBiodiversity.page';
import { LandCarbonPage } from '@/pages/shared/land/LandCarbon.page';
import { LandSocialPage } from '@/pages/shared/land/LandSocial.page';
import { LandSoilPage } from '@/pages/shared/land/LandSoil.page';
import { LandWaterPage } from '@/pages/shared/land/LandWater.page';
import { MediaUploadPage } from '@/pages/shared/media-upload/MediaUpload.page';
import { PlotOverviewPage } from '@/pages/shared/media-upload/pages/PlotOverview.page';
import { OrganizationOnboardingPage } from '@/pages/shared/organization/pages/Onboarding.page';
import { InteractiveMapPage } from '@/pages/shared/plots/pages/InteractiveMap.page';
import { PlotGroupsPage } from '@/pages/shared/plots/PlotGroups.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { ContractsPage } from '@/pages/shared/portfolio/pages/contracts/Contracts.page';
import { PortfolioNaturalCapitalPage } from '@/pages/shared/portfolio/pages/NaturalCapital/PortfolioNaturalCapital.page';
import { ScenarioForecastsPage } from '@/pages/shared/portfolio/pages/scenarios/ScenarioForecasts.page';
import { StocksPage } from '@/pages/shared/portfolio/pages/stocks/Stocks.page';
import { UpliftUnitsPage } from '@/pages/shared/portfolio/pages/uplift/UpliftUnits.page';
import { PortfolioInvestmentsPage } from '@/pages/shared/portfolio/PortfolioInvestments.page';
import { DetailsPage } from '@/pages/shared/projects/pages/project/Details.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { WaterDetailPage as WaterDetailConservationPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/water/WaterDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { ReportsPage } from '@/pages/shared/reports/Reports.page';
import { NdiProductsPage } from '@/pages/shared/staff-portal/pages/ndi-products/NdiProducts.page';
import { OrganizationNdiSubscriptionsPage } from '@/pages/shared/staff-portal/pages/organization-ndi/OrganizationNdiSubscriptions.page';
import { EditJobSubscriptionPage } from '@/pages/shared/staff-portal/pages/organization-ndi/pages/EditJobSubscription.page';
import { EditPlotPortfolioSubscriptionPage } from '@/pages/shared/staff-portal/pages/organization-ndi/pages/EditPlotPortfolioSubscription.page';
import { NdiFinancialPage } from '@/pages/shared/staff-portal/pages/organization-ndi/pages/NdiFinancial.page';
import { OrganizationsPage } from '@/pages/shared/staff-portal/pages/organizations/Organizations.page';
import { UploadCoordinatesPage } from '@/pages/shared/staff-portal/pages/upload-plots/UploadCoordinates.page';
import { UploadPlotsPage } from '@/pages/shared/staff-portal/pages/upload-plots/UploadPlots.page';
import { InSituDataPage } from '@/pages/shared/uploads/pages/InSituData.page';
import { MediaMapsPage } from '@/pages/shared/uploads/pages/MediaMaps.page';
import { MediaMapsConfigurationPage } from '@/pages/shared/uploads/pages/pages/MediaMapsConfiguration.page';
import { UploadsPage } from '@/pages/shared/uploads/Uploads.page';
import { AddWorkstreamPage } from '@/pages/shared/workstreams/pages/AddWorkstream.page';
import { EditWorkstreamPage } from '@/pages/shared/workstreams/pages/EditWorkstream.page';
import { WorkstreamsPage } from '@/pages/shared/workstreams/Workstreams.page';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { HomePageProtection } from '@/routing/components/protections/home-page/HomePageProtection';
import { PlotGroupDetailsCleaner } from '@/routing/components/protections/PlotGroupDetailsCleaner/PlotGroupDetailsCleaner';
import { RequireAccessiblePlot } from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { NoGroupByProtection } from '@/routing/components/protections/require-group-by/NoGroupByProtection';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { UploadsProtection } from '@/routing/components/protections/require-uploads/UploadsProtection';
import { paths } from '@/routing/routing';

import { AccountPageWrapper } from '../../account/components/Account.page-wrapper';
import { ComplianceFrameworksPageWrapper } from '../../compliance-frameworks/components/ComplianceFrameworks.page-wrapper';
import { GlobalCopilotPage } from '../../copilot/GlobalCopilot.page';
import { ForecastsPage } from '../../portfolio/pages/forecasts/Forecasts.page';
import { PlotPage } from '../../projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { OrganizationPage } from '../pages/organization/Organization.page';
import { StaffSignedInRoute } from './components/StaffSignedInRoute';

export const StaffPortalRoutes = (
  <Route path='staff'>
    <Route element={<StaffSignedInRoute />}>
      <Route index element={<Navigate to={paths.staff.organizations} />} />
      <Route path={paths.staff.organizations} element={<OrganizationsPage />} />
      <Route path={paths.staff.globalCopilot} element={<GlobalCopilotPage />} />
      <Route element={<ResponsiveLayout showImpersonatingBanner={false} />}>
        <Route element={<PlotGroupDetailsCleaner />}>
          <Route path={paths.staff.account} element={<AccountPageWrapper />}>
            <Route index element={<AccountPage />} />
            <Route path={paths.staff.accountEditEmail} element={<AccountEditEmailPage />} />
            <Route path={paths.staff.accountEditName} element={<AccountEditNamePage />} />
            <Route path={paths.staff.accountEditPassword} element={<AccountEditPasswordPage />} />
            <Route path={paths.staff.accountLegal} element={<AccountLegalPage />} />
            <Route path={paths.staff.accountTeam}>
              <Route index element={<AccountTeamPage />} />
              <Route element={<RequireAdminRole />}>
                <Route path={paths.staff.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
                <Route path={paths.staff.accountTeamInvite} element={<AccountTeamInvitePage />} />
              </Route>
            </Route>
          </Route>

          <Route path={paths.staff.socialDetails} element={<LandSocialPage />} />

          <Route path={paths.staff.portfolio} element={<LandstewardPortfolioPage />} />

          <Route path={paths.staff.portfolioNaturalCapital} element={<PortfolioNaturalCapitalPage />} />
          <Route path={paths.staff.portfolioForecasts} element={<ForecastsPage />} />
          <Route path={paths.staff.portfolioInvestments} element={<PortfolioInvestmentsPage />} />
          <Route path={paths.staff.portfolioUnits} element={<UpliftUnitsPage />} />
          <Route path={paths.staff.portfolioStocks} element={<StocksPage />} />
          <Route path={paths.staff.portfolioContracts} element={<ContractsPage />} />

          <Route path={`${paths.staff.scenarioModels}/*`} element={<ScenarioForecastsPage />} />

          <Route path={`${paths.staff.disclosure}/*`} element={<DisclosurePage />} />

          <Route path={paths.staff.investments} element={<LandstewardInvestmentsPage />} />
          <Route element={<RequireSupportPlot />}>
            <Route path={paths.staff.plots} element={<PlotsPage />} />
            <Route element={<NoGroupByProtection />}>
              <Route path={paths.staff.groups} element={<PlotGroupsPage />} />
              <Route path={paths.staff.comparisons} element={<CompareListPage />} />
              <Route path={paths.staff.compare} element={<ComparePage />} />
            </Route>
          </Route>
          <Route index element={<Navigate to={paths.staff.projects} replace />} />

          <Route element={<NoGroupByProtection />}>
            <Route path={`${paths.staff.details}/*`} element={<DetailsPage />} />
            <Route path={paths.staff.biodiversityDetails} element={<LandBiodiversityPage />} />
            <Route path={paths.staff.soilDetails} element={<LandSoilPage />} />
            <Route path={paths.staff.carbonDetails} element={<LandCarbonPage />} />
            <Route path={paths.staff.waterDetails} element={<LandWaterPage />} />
          </Route>

          <Route path={paths.staff.projects}>
            <Route path={paths.staff.newProject} element={<ProjectsNewPage />} />
            <Route index element={<ProjectsPage />} />
            <Route path={paths.staff.projectsComplianceFrameworks} element={<ComplianceFrameworksPageWrapper />}>
              <Route index element={<ComplianceFrameworksPage />} />
            </Route>
            <Route element={<RequireAccessibleProject />}>
              <Route path={paths.staff.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
              <Route path={paths.staff.habitatIntactness} element={<HabitatIntactnessPages />} />
              <Route path={paths.staff.indicatorSpecies} element={<IndicatorSpeciesPages />} />
              <Route path={paths.staff.ndvi} element={<NDVIDetailPage />} />
              <Route path={`${paths.staff.projectDetails}/*`} element={<ProjectPage />} />
              <Route path={paths.staff.interactiveMap} element={<InteractiveMap />} />
              <Route path={paths.staff.projectDetailsWater} element={<WaterDetailConservationPage />} />
              <Route path={`${paths.staff.editPlot}/*`} element={<LandstewardEditPlotPage />} />
              <Route path={`${paths.staff.newPlot}/*`} element={<LandstewardNewPlotPage />} />
            </Route>
          </Route>
          <Route element={<RequireAccessiblePlot />}>
            <Route path={`${paths.staff.plot}/*`} element={<PlotPage />} />
            <Route path={paths.staff.plotInteractiveMap} element={<InteractiveMapPage />} />
          </Route>
          <Route path={`${paths.staff.editPlotOrg}/*`} element={<LandstewardEditPlotPage />} />

          <Route path={paths.staff.restrictedAccess} element={<RestrictedAccessPage />} />
          <Route path={paths.staff.indicators} element={<IndicatorsPage />} />
          <Route element={<HomePageProtection />}>
            <Route path={paths.staff.home} element={<HomePage />} />
          </Route>

          <Route path={paths.staff.contracts} element={<ContractsPage />} />
          <Route path={paths.staff.reports} element={<ReportsPage />} />

          <Route path={paths.staff.copilot} element={<CopilotPage />} />
          <Route path={paths.staff.knowledge} element={<KnowledgePage />} />
          <Route path={paths.staff.workstreams} element={<WorkstreamsPage />} />
          <Route path={paths.staff.workstreamsAdd} element={<AddWorkstreamPage />} />
          <Route path={paths.staff.workstreamsEdit} element={<EditWorkstreamPage />} />
          <Route path={paths.staff.ndiProducts} element={<NdiProductsPage />} />
          <Route element={<UploadsProtection />}>
            <Route path={paths.staff.uploads} element={<UploadsPage />} />
            <Route path={paths.staff.uploadsPlotOverview} element={<MediaUploadPage />} />
            <Route path={paths.staff.uploadsPlotOverviewPlot} element={<PlotOverviewPage />} />
            <Route path={paths.staff.insituDataUpload} element={<InSituDataPage />} />
            <Route path={paths.staff.mediaMaps} element={<MediaMapsPage />} />
            <Route path={paths.staff.mediaMapsConfiguration} element={<MediaMapsConfigurationPage />} />
          </Route>
          <Route path={paths.staff.plotUpload} element={<UploadPlotsPage />} />
          <Route path={paths.staff.plotCoordinateUpload} element={<UploadCoordinatesPage />} />
          <Route path={paths.staff.organization} element={<OrganizationPage />} />
          <Route path={paths.staff.organizationNdiSubscriptions} element={<OrganizationNdiSubscriptionsPage />} />
          <Route path={paths.staff.organizationNdiSubscriptionEdit} element={<EditJobSubscriptionPage />} />
          <Route
            path={paths.staff.organizationNdiPlotPortfolioSubscriptionEdit}
            element={<EditPlotPortfolioSubscriptionPage />}
          />
          <Route path={paths.staff.organizationNdiFinancial} element={<NdiFinancialPage />} />
        </Route>
      </Route>
      <Route path={paths.staff.organizationOnboarding} element={<OrganizationOnboardingPage />} />
    </Route>
  </Route>
);
